import { NgClass } from "@angular/common";
import { Component, ElementRef, Input, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { NavigateFromCatalogSearchToCatalogSearchResultParams, NavigateLinkedCatalogParams } from "@app/models/tracking/track-history-param-type";
import { SearchbarPublicBase } from "@app/super/searchbar-public-base";
import { CatalogLogoImgComponent } from "@commonComponents/catalog-logo-img/catalog-logo-img.component";
import { isNullish } from "@commonHelpers/math-utils";
import { NgbTooltip } from "@commonNodeModules/@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@commonNodeModules/@ngx-translate/core";
import { SearchHighLightPipe } from "@commonPipes/searchHightlight";
import { CatalogSearchResultDisplayType } from "@enums/catalog-search-result-display-type";
import { ViewType } from "@enums/view-type";
import { CatalogViewRelatedCatalogsDto } from "@interfaces/HttpClient/CatalogApiPublicModels";
import { AnalyticsTrackingFacadeService } from "@services/analytics-tracking-facade.service";
import { BreadcrumbService } from "@services/breadcrumb.service";
import { CatalogService } from "@services/catalog.service";
import { IntervalService } from "@services/interval.service";
import { SearchResultService } from "@services/search-result.service";
import { SearchService } from "@services/search.service";
import { SearchbarService } from "@services/searchbar.service";
import { TranslationService } from "@services/translation.service";
import { ViewTypeService } from "@services/view-type.service";

@Component({
	selector: 'app-catalog-searchbar',
	templateUrl: './catalog-searchbar.component.html',
	styleUrls: ['./catalog-searchbar.component.scss'],
	providers: [CatalogService],
	imports: [NgbTooltip, FormsModule, NgClass, TranslatePipe, SearchHighLightPipe, CatalogLogoImgComponent]
})
export class CatalogSearchbarComponent extends SearchbarPublicBase implements OnInit, OnDestroy {
	@Input() canNavigateToStartingPage: boolean
	@Input() catalogKey: string;
	@Input() relatedCatalogs: CatalogViewRelatedCatalogsDto[];

	public viewType = ViewType;

	constructor(
		protected searchbarService: SearchbarService,
		private searchService: SearchService,
		public searchResultService: SearchResultService,
		protected breadcrumbService: BreadcrumbService,
		protected router: Router,
		protected elRef: ElementRef,
		protected catalogService: CatalogService,
		protected viewTypeService: ViewTypeService,
		protected translationService: TranslationService,
		protected trackingFacadeService: AnalyticsTrackingFacadeService,
		private intervalService: IntervalService
	) { super(translationService, breadcrumbService, router, searchbarService, elRef, viewTypeService) }

	ngOnInit() {
		this.init();
		this.intervalService.getInterval().subscribe(() => this.sendNewTrackedElements());
	}

	async ngOnDestroy(): Promise<void> {
		this.destroy();
		this.sendNewTrackedElements();
	}

	public async navigateToCatalog(currentKey?: string) {
		const key = isNullish(this.indexedCatalogKey) ? currentKey : this.indexedCatalogKey;
		// Tracking: set searchSuggestionSource
		const searchTerm = this.currentSearchWord ?? this.currentSearchTerm

		this.trackingFacadeService.handleTrackHistory(new NavigateLinkedCatalogParams())

		this.trackBeforeNavigate();
		await this.searchService.navigateToCatalog({
			catalogKey: key,
			searchTerm,
			searchFilter: "",
			pageIndex: 0,
			pageSize: 30
		});
	}

	public catalogSuggestionClicked(): void {
		this.breadcrumbService.setCatalogNavigation(undefined);
		this.breadcrumbService.setSearchNavigation(undefined)
	}

	protected async navigateToSearch(term, isInput?: boolean, isSuggestionClicked?: boolean) {
		this.trackingFacadeService.handleTrackHistory(new NavigateFromCatalogSearchToCatalogSearchResultParams(this.indexedSearchterm, term, isSuggestionClicked))

		term = term.trim()
		if (!term || term.length === 0) {
			if (isInput) {
				this.searchbarService.searchTerm = "";
				this.emptySearch.emit();
			}
			this.closeSuggestions();
			return;
		}

		const displayType = this.currentViewType === ViewType.Desktop ? CatalogSearchResultDisplayType.Catalog : CatalogSearchResultDisplayType.List;

		this.trackBeforeNavigate();
		await this.searchService.navigateToCatalog({
			catalogKey: this.catalogKey,
			searchTerm: term,
			searchFilter: "",
			pageIndex: 0,
			pageSize: 30
		}, displayType);

		this.breadcrumbService.setCatalogNavigation(this.breadcrumbService.getSearch({ searchTerm: term, searchFilter: undefined, searchPage: undefined }));
		this.closeSuggestions();
	}

	public get placeholder() {
		return this.searchbarService.searchTerm ? this.translationService.getByKey('searchbar.current-searchterm', { value: this.searchbarService.searchTerm }) : this.translationService.getByKey('catalog-view.searchbar.search-term')
	}

	public async changeSearchSuggestions(value: string) {
		this.focusToInput();
		this.suggestions = await this.searchService.getCatalogSuggestions(this.catalogKey, this.currentSearchWord)
		if (!isNullish(this.suggestions)) {
			this.suggestedTerms = this.suggestions?.suggestedTerms?.slice(0, this.SUGGESTIONCOUNT);
		}
		this.currentSearchTerm = value;
		const lastTerm = this.searchbarService.lastSearchResultTerm
		if (!isNullish(lastTerm) && !this.suggestedTerms?.includes(lastTerm)) {
			this.suggestedTerms?.unshift(lastTerm);
		}
		if (!isNullish(this.currentSearchWord) && this.currentSearchWord !== "") {
			if (this.relatedCatalogs?.length > 0) {
				this.suggestedCatalogs = this.relatedCatalogs?.slice(0, this.CATALOGCOUNT)
			}
			if (this.canNavigateToStartingPage) {
				this.additionalItems = this.categoryGroups
					?.map(item => ({
						id: item.id,
						description: this.translationService.getByKey('searchbar.search-term', { searchTerm: this.currentSearchWord }),
						logo: this.catalogService.getCategoryGroupLogo(item.id),
						title: item.title,
						functionality: 'category'
					})) ?? []
				this.additionalItems.push({
					description: this.translationService.getByKey('searchbar.search-term', { searchTerm: this.currentSearchWord }),
					title: this.translationService.getByKey('footer.address'),
					logo: '/assets/orca/logosmall.png',
					functionality: 'global',
					id: -1
				})
			}


			this.suggestedCatalogs?.forEach(catalog => {
				if (!this.loadedListItems?.includes(catalog.key)) {
					this.loadedListItems?.push(catalog.key)
				}
			})


		} else {
			this.additionalItems = [];
			this.suggestedCatalogs = [];
		}

	}

	public async searchItem(term: string, isSuggestionClicked: boolean) {
		await this.navigateToSearch(term, null, isSuggestionClicked);
		this.searchService.refresh();
		this.closeSuggestions();
	}
}
